<template>
    <div
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/20"
        @click.self="$emit('close')"
    >
        <div class="bg-white rounded-xl border border-gray-200 w-full max-w-lg mx-4 shadow-2xl">
            <!-- Header -->
            <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h2 class="text-lg font-semibold text-gray-900"><i class="fa-duotone fa-solid fa-id-badge text-primary-500 mr-2"></i>Writing Profile</h2>
                <button
                    class="text-gray-400 hover:text-gray-600 transition-colors"
                    @click="$emit('close')"
                >
                    <i class="fa-solid fa-xmark text-lg"></i>
                </button>
            </div>

            <!-- Content -->
            <div class="px-6 py-4 overflow-y-auto max-h-[70vh] space-y-6">
                <!-- Loading state -->
                <div v-if="loading" class="text-center text-gray-400 py-8">
                    Loading...
                </div>

                <template v-else>
                    <!-- Tone Preset -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-3"><i class="fa-solid fa-volume text-gray-400 mr-1"></i> Tone Preset</label>
                        <div class="grid grid-cols-2 gap-2">
                            <button
                                v-for="(label, key) in tonePresets"
                                :key="key"
                                class="px-3 py-2.5 rounded-lg border text-sm font-medium transition-all text-left"
                                :class="profile.tone_preset === key
                                    ? 'bg-primary-50 border-primary-500 text-primary-700 ring-2 ring-primary-500/20'
                                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-100'"
                                @click="profile.tone_preset = key"
                            >
                                {{ label }}
                            </button>
                        </div>
                    </div>

                    <!-- Example Emails -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1"><i class="fa-solid fa-envelopes text-gray-400 mr-1"></i> Example Emails</label>
                        <p class="text-xs text-gray-400 mb-3">Paste 1-3 emails you've sent before. The AI will match your voice.</p>
                        <div class="space-y-3">
                            <div
                                v-for="(example, index) in profile.example_emails"
                                :key="index"
                                class="space-y-1"
                            >
                                <div class="flex items-center justify-between">
                                    <span class="text-xs text-gray-500 font-medium">Example {{ index + 1 }}</span>
                                    <button
                                        class="text-xs text-red-500 hover:text-red-600 transition-colors"
                                        @click="removeExample(index)"
                                    >
                                        Remove
                                    </button>
                                </div>
                                <textarea
                                    v-model="profile.example_emails[index]"
                                    rows="4"
                                    class="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 resize-y"
                                    placeholder="Paste an example email here..."
                                ></textarea>
                            </div>
                        </div>
                        <button
                            v-if="profile.example_emails.length < 3"
                            class="mt-2 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors"
                            @click="addExample"
                        >
                            + Add example email
                        </button>
                    </div>

                    <!-- Signature Block -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2"><i class="fa-solid fa-signature text-gray-400 mr-1"></i> Signature Block</label>
                        <textarea
                            v-model="profile.signature_block"
                            rows="3"
                            class="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 resize-y"
                            placeholder="Your Name&#10;Side-Out Foundation"
                        ></textarea>
                    </div>
                </template>
            </div>

            <!-- Footer -->
            <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
                <button
                    class="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                    @click="$emit('close')"
                >
                    Cancel
                </button>
                <button
                    class="px-5 py-2 text-sm font-semibold bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    :disabled="saving || loading"
                    @click="save"
                >
                    <i v-if="saving" class="fa-solid fa-spinner-third fa-spin mr-1"></i>
                    <i v-else class="fa-solid fa-floppy-disk mr-1"></i>
                    {{ saving ? 'Saving...' : 'Save Profile' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'WritingProfileModal',
    emits: ['close', 'saved'],
    data() {
        return {
            profile: { tone_preset: 'warm_personal', example_emails: [], signature_block: '' },
            tonePresets: {},
            saving: false,
            loading: true,
        };
    },
    mounted() {
        this.fetchProfile();
    },
    methods: {
        async fetchProfile() {
            try {
                const response = await fetch('/admin/email-composer/profile');
                const json = await response.json();
                if (json.profile) {
                    this.profile = {
                        tone_preset: json.profile.tone_preset || 'warm_personal',
                        example_emails: json.profile.example_emails || [],
                        signature_block: json.profile.signature_block || '',
                    };
                }
                if (json.tone_presets) {
                    this.tonePresets = json.tone_presets;
                }
            } catch (error) {
                console.error('Failed to load writing profile:', error);
            } finally {
                this.loading = false;
            }
        },
        addExample() {
            if (this.profile.example_emails.length < 3) {
                this.profile.example_emails.push('');
            }
        },
        removeExample(index) {
            this.profile.example_emails.splice(index, 1);
        },
        async save() {
            this.saving = true;
            try {
                const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
                const response = await fetch('/admin/email-composer/profile', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken },
                    body: JSON.stringify(this.profile),
                });
                if (response.ok) {
                    this.$emit('saved');
                }
            } catch (error) {
                console.error('Failed to save writing profile:', error);
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>
